import { useState, useEffect } from 'react';
import { Alert, Text, Group, Button, Menu } from '@mantine/core';
import { AlertCircle, ChevronDown } from 'react-feather';
import {
  coachPortalApi,
  type SessionNeedingAttendance,
} from '../../../services/api/coachPortal';
import { sessionGroupName } from '../utils/sessionLabel';
import classes from './AttendanceNeededBanner.module.css';

interface AttendanceNeededBannerProps {
  /** Callback when user wants to mark attendance for a session */
  onMarkAttendance?: (session: SessionNeedingAttendance) => void;
  /** Optional: filter to specific cohort */
  cohortId?: string;
}

export function AttendanceNeededBanner({
  onMarkAttendance,
  cohortId,
}: AttendanceNeededBannerProps) {
  const [sessions, setSessions] = useState<SessionNeedingAttendance[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPendingAttendance = async () => {
      try {
        const response = await coachPortalApi.getPendingAttendance();
        const filteredSessions = cohortId
          ? response.sessions.filter((s) => s.cohort_id === cohortId)
          : response.sessions;
        setSessions(filteredSessions);
      } catch (error) {
        console.error('Failed to fetch pending attendance:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingAttendance();
  }, [cohortId]);

  if (loading || sessions.length === 0) {
    return null;
  }

  const sessionCount = sessions.length;
  const isMultiple = sessionCount > 1;

  // MED-618: Check if any session is 24h+ overdue for visual escalation
  const HOURS_24_MS = 24 * 60 * 60 * 1000;
  const hasOverdue = sessions.some(
    (s) => Date.now() - new Date(s.scheduled_end).getTime() >= HOURS_24_MS,
  );
  const bannerColor = hasOverdue ? 'red' : 'orange';

  const handleSingleClick = () => {
    if (onMarkAttendance) {
      onMarkAttendance(sessions[0]);
    }
  };

  return (
    <Alert
      className={classes.banner}
      color={bannerColor}
      variant="light"
      mb="lg"
      styles={{
        root: {
          borderLeft: `4px solid var(--mantine-color-${bannerColor}-6)`,
        },
      }}
    >
      <Group justify="space-between" wrap="nowrap" className={classes.row}>
        <Group gap="sm" wrap="nowrap">
          <AlertCircle size={20} />
          <div>
            <Text fw={600} size="sm">
              {hasOverdue ? 'Attendance Overdue' : 'Attendance Needed'}
            </Text>
            <Text size="sm" c="dimmed">
              {isMultiple
                ? `You have ${sessionCount} sessions that need attendance marked.`
                : sessionGroupName(sessions[0].group_name)
                  ? `"${sessions[0].title}" (${sessionGroupName(sessions[0].group_name)}) needs attendance marked.`
                  : `"${sessions[0].title}" needs attendance marked.`}
            </Text>
          </div>
        </Group>

        {onMarkAttendance && (
          isMultiple ? (
            <Menu position="bottom-end" shadow="md" width={320}>
              <Menu.Target>
                <Button
                  className={classes.action}
                  size="xs"
                  color={bannerColor}
                  variant="filled"
                  rightSection={<ChevronDown size={14} />}
                >
                  View Sessions
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Select a session to mark attendance</Menu.Label>
                {sessions.map((session) => (
                  <Menu.Item
                    key={session.id}
                    onClick={() => onMarkAttendance(session)}
                  >
                    <Text size="sm" fw={500} lineClamp={1}>
                      {session.title}
                    </Text>
                    {sessionGroupName(session.group_name) && (
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {sessionGroupName(session.group_name)}
                      </Text>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          ) : (
            <Button
              className={classes.action}
              size="xs"
              color={bannerColor}
              variant="filled"
              onClick={handleSingleClick}
            >
              Mark Attendance
            </Button>
          )
        )}
      </Group>
    </Alert>
  );
}

export default AttendanceNeededBanner;
